import { Outlet, useLocation, useParams } from 'react-router-dom'
import DriveHeaderV2 from './DriveHeaderV2'
import { paths } from '@/utils/path'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { extractObjectName } from '@/modules/Drive/helpers/helper'
import {
    GetEmployeeNameByUuid,
    ListStarredObjectKeys,
} from '@/modules/Drive/services'
import useAuthStore from '@/stores/useAuthStore'
import useDriveStore from '@/stores/useDriveStore'
import { isValidUUID } from '@/modules/Drive/utils'

const Dashboard = () => {
    const location = useLocation()
    const { '*': folderPath } = useParams()
    const { t } = useTranslation()
    const employee = useAuthStore((state) => state.employee)
    const setStarredObjectKeys = useDriveStore(
        (state) => state.setStarredObjectKeys
    )
    const appendItemName = useDriveStore((state) => state.appendItemName)
    const itemNames = useDriveStore((state) => state.itemNames)
    const setIsSelectedMode = useDriveStore((state) => state.setIsSelectedMode)
    // Section mapping for breadcrumb titles using translations
    const getSectionTitle = (section: string) => {
        switch (section) {
            case 'my-drive':
                return t('my-drive-title')
            case 'starred':
                return t('starred-files-title')
            case 'shared':
                return t('shared-with-me-title')
            case 'root':
                return t('root-drive-title')
            case 'recent':
                return t('recent-file-title')
            default:
                return section
        }
    }

    const breadcrumbs = useMemo(() => {
        const baseBreadcrumbs = [
            {
                path: paths.drive,
                title: 'Drive',
            },
        ]

        // Get the current section from the path
        const pathSegments = location.pathname.split('/').filter(Boolean)
        const driveIndex = pathSegments.findIndex(
            (segment) => segment === 'drive'
        )
        const currentSection = pathSegments[driveIndex + 1]

        // Add section breadcrumb if we're in a specific section
        if (currentSection) {
            const sectionTitle = getSectionTitle(currentSection)
            if (sectionTitle !== currentSection) {
                baseBreadcrumbs.push({
                    path: `${paths.drive}/${currentSection}` as any,
                    title: sectionTitle,
                })
            }
        }

        // Add folder breadcrumbs if we're in a folder
        if (folderPath && folderPath.length > 0) {
            const folderSegments = folderPath.split('/').filter(Boolean)

            // Build breadcrumbs for each folder level
            folderSegments.forEach(async (_, index) => {
                let currentPath = folderSegments.slice(0, index + 1).join('/')
                const fullPath = `${paths.drive}/folders/${currentPath}` as any
                if (index === 0) {
                    currentPath = 'pub/drives/' + currentPath
                }
                const folderName = extractObjectName(currentPath)
                let breadcrumbPath = folderName

                if (isValidUUID(folderName)) {
                    if (itemNames[folderName]) {
                        breadcrumbPath = itemNames[folderName]
                    } else {
                        const result = await GetEmployeeNameByUuid(folderName)
                        if (result.result) {
                            breadcrumbPath = result.result
                            appendItemName(folderName, result.result)
                        }
                    }
                }

                baseBreadcrumbs.push({
                    path: fullPath,
                    title: breadcrumbPath,
                })
            })
        }

        return baseBreadcrumbs
    }, [location.pathname, folderPath, t, itemNames])

    useEffect(() => {
        const fetchStarredObjectKeys = async () => {
            await ListStarredObjectKeys(employee?.employee_uuid).then((res) =>
                setStarredObjectKeys(res.results)
            )
        }
        fetchStarredObjectKeys()
    }, [employee])

    useEffect(() => {
        return () => {
            setIsSelectedMode(false)
        }
    }, [])

    return (
        <>
            <SubHeaders breadcrumbs={breadcrumbs} />
            <DriveHeaderV2>
                <div className="mt-2">
                    <Outlet />
                </div>
            </DriveHeaderV2>
        </>
    )
}

export default Dashboard
